/** The width the Audience Layer panel is designed at. Mirrors `.audience-layer-panel`. */
export const AL_PANEL_WIDTH = 380;

/**
 * The width below which the Audience Layer stops sitting beside the video and
 * takes a full-width row underneath it.
 *
 * There are two breakpoints in play and they were not the same one, which is
 * why this constant exists. The Watch page's grid stacked at MUI's `md`, 900px;
 * the panel's own stylesheet switched to its stacked rules at 1023px. Between
 * those two numbers the columns were still side by side while the CSS believed
 * they were stacked, so on a 912px tablet the panel was a ~260px column trying
 * to hold a 380px design.
 *
 * One number now, used by both. `AudienceLayerPanel.css` cannot import it, so
 * its media query names this file; change one and change the other.
 */
export const AL_STACK_BREAKPOINT = 1080;

/*
 * Beside the video, the sidebar is 380px. Not three and a half twelfths.
 *
 * THE COLUMN FRACTION WAS THE CAUSE OF THE CRAMPED PANEL, not the breakpoint.
 * A moment card is [tile][text][bookmark][download][menu]: everything but the
 * text is a fixed size, so the text gets whatever is left over after about
 * 200px. At 3.5 of 12 the panel is 361px on a 1440px window and 315px on a
 * 1080px one, and the title column comes out at 111px and 76px respectively,
 * which is where "Opening goal, top shelf" became "Openin...". Measured in the
 * browser rather than reasoned about.
 *
 * A fraction can only be right at one window width. The panel has a width it
 * wants, so the column is given that width and the video takes whatever
 * remains, which is what a video is happy to do and a list of cards is not.
 *
 * WHY PROP OBJECTS RATHER THAN AN sx OVERRIDE. The first attempt kept
 * `md={3.5}` and added `sx={{ flexBasis: 380 }}`. Both rules landed on the
 * element and MUI's `MuiGrid-grid-md-3.5` won, because it sits in a
 * `@media (min-width:900px)` block inserted after the sx class: equal
 * specificity, later in the sheet. Rather than escalate with `!important`,
 * each branch below sets the width from exactly one source, so there is
 * nothing for the cascade to arbitrate.
 */

/**
 * The gutter `<Grid container spacing={1}>` puts on each item.
 *
 * Grid items are border-box, so the gutter comes out of the column's width
 * rather than sitting beside it: a 380px basis leaves the panel 372px and it
 * never reaches its own max-width. Added back here so Classic View gives the
 * panel the same 380px Enhanced View does.
 */
const AL_GRID_GUTTER = 8;

/** The sidebar column beside the video: its own width, no grid fraction. */
export function alSidebarGridProps(sideBySide: boolean, stacked: boolean) {
  if (sideBySide) {
    const basis = `${AL_PANEL_WIDTH + AL_GRID_GUTTER}px`;
    // `position: relative` makes this the containing block for the panel, which
    // is absolutely positioned at this width (see AudienceLayerPanel.css). In
    // flow the panel's own conversation sized the grid row, which left the
    // video column short beside it.
    return {
      sx: {
        flexGrow: 0,
        flexBasis: basis,
        maxWidth: basis,
        position: "relative",
      },
    };
  }
  // Stacked (or the layer is not the thing in this slot): the ordinary
  // twelfths, full width once the page has stacked.
  return { xs: 12, md: stacked ? 12 : 3.5 };
}

/** Its partner: the video takes everything the sidebar did not. */
export function alVideoGridProps(
  sideBySide: boolean,
  stacked: boolean,
  enhanced: boolean,
) {
  if (sideBySide) {
    /*
     * `minWidth: 0` is what keeps the sidebar on this row.
     *
     * A flex item defaults to `min-width: auto`, meaning it will not shrink
     * below its own content. The clips strip holds a horizontally scrolling
     * row whose content is several thousand pixels wide, so without this the
     * column claims all of it, and a wrapping container has nowhere to put the
     * sidebar but the next line. With it, the column takes the space left over
     * and the card row scrolls inside, which is what the scroller is for.
     */
    return {
      sx: {
        flexGrow: 1,
        flexBasis: 0,
        maxWidth: "100%",
        minWidth: 0,
        // The column spaces its own children, so the player and the clip bar
        // carry no margins of their own. The inset is left to the grid: with
        // spacing={1} MUI gives every item padding-left and padding-top and
        // nothing else, so adding px here would give this column a right
        // padding the sidebar beside it does not have.
        display: "flex" as const,
        flexDirection: "column" as const,
        gap: "8px",
      },
    };
  }
  return {
    xs: 12,
    md: enhanced || stacked ? 12 : 8.5,
    sx: {
      minWidth: 0,
      display: "flex" as const,
      flexDirection: "column" as const,
      gap: "8px",
    },
  };
}
